import {FixedSizeList as Lists} from 'react-window';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {SelectDropdownRenderTags} from './SelectDropdownRenderTags';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {makeStyles, styled} from '@mui/styles';
import dayjs, {Dayjs} from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {useQueryClient} from '@tanstack/react-query';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {DateRange} from '@mui/lab';
import {UploadFileOutlined, Warning, WarningAmber, DoNotDisturb} from '@mui/icons-material';
import {DateRangePicker} from '@mui/x-date-pickers-pro';
import {
  StepperLayout,
  useStepperState,
} from '../../../components/layouts/stepper/StepperLayout';
import DialogApiRequestStatus from '../../../components/dialogs/DialogApiRequestStatus';
import {
  IState,
  useRqListStates,
} from '../../../react_query/product-representatives/ListProdRepresentative';
import {
  IListAllCompaniesQueryParams,
  useRqListAllCompaniesList,
} from '../../../react_query/companies/CompanyWithoutPagination';
import {ICompany} from '../../../dto/Company.dto';
import {apiPost} from '../../../axiosConfig';
import {
  getTnnCampaignTypesUrl,
  useRqListTnnCampaignSchedule,
} from '../../../react_query/tnn_sponsors/TnnCampaignTypes';
import {SelectNameAndTypeCmpt} from './TnnCmpt/SelectNameAndTypeCmpt';
import {SelectPartners} from './TnnCmpt/SelectPartners';
import {ReviewCmpt} from './TnnCmpt/ReviewCmpt';
import {IMediaTrack} from '../../../react_query/miniPlayerQuery';
import {IOSSwitch} from '../../../components/styles/ToggleButton';
import {getAudioDurationFromFile} from '../../../components/utlis/Audio/GetAudioDuration';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import {UploadProcessedFileDialog} from '../../messages/timed-message/request/contents/UploadProcessedFileDialog';
import {useListStyles} from '../../../components/MUI/SplitSectionSelection/SplitSectionStyle';
import Toast from '../../../package/Toaster';
import {
  alertConfig,
  TNN_PERCENTAGE_DEFAULT,
} from '../../../config/messageConfig';

export type ISlotSelectionProp = {
  tnn_campaign_slot: number;
  tnn_campaign_weightage: number;
  tnn_campaign_is_primary: boolean;
  label: string;
  file: File | undefined;
  media_track?: IMediaTrack;
  fileName?: string;
  initial_url?: string;
};

export type IParameterDetailsProp = {
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  number_of_runs: number;
  slots: number | null;
  slot_details: ISlotSelectionProp[];
  commercial_length: string;
  commercial_length_other: string;
  ad_source: string;
  manual: boolean;
};

export interface ICompanyWithPricing extends ICompany {
  share_price?: number;
  dollar_value?: number;
}

// Type for company from API with pivot data
interface ICompanyFromAPI extends ICompany {
  pivot?: {
    share_price?: string;
    dollar_value?: string;
  };
}

type IPartnersDetailsProp = {
  selected_companies: ICompanyWithPricing[];
};
export type IPartnerProps = {
  gametime: IPartnersDetailsProp;
  neptuneRadio: IPartnersDetailsProp;
};
export type IKeyPartnersProps = keyof IPartnerProps;

export const defaultParameters = {
  start_date: null,
  end_date: null,
  number_of_runs: 1,
  slots: null,
  slot_details: [],
  commercial_length: '',
  commercial_length_other: '',
  ad_source: '',
  manual: false,
};

export const defaultPartners = {
  gametime: {selected_companies: []},
  neptuneRadio: {selected_companies: []},
};

const TnnCampaignCreationStepper = ({
  sponsorRequestId,
  scheduleId = 0,
  closeDialog,
}: {
  sponsorRequestId: number;
  scheduleId?: number;
  closeDialog: () => void;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [parameter, setParameter] =
    useState<IParameterDetailsProp>(defaultParameters);
  const [partners, setPartners] = useState<IPartnerProps>(defaultPartners);
  const {activeStepIndex, onNextStep, onPreviousStep, onStepClick} =
    useStepperState(0);
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState<string[]>([]);
  const [tnnPercentageValue, setTnnPercentageValue] = useState(
    TNN_PERCENTAGE_DEFAULT,
  );
  const isEditStepper = Boolean(scheduleId);
  const [adSource, setAdSource] = useState('NA');
  const [commercialLength, setCommercialLength] = useState('NA');
  const [type, setType] = useState('V');
  const {data, isFetching} = useRqListTnnCampaignSchedule(
    sponsorRequestId,
    scheduleId,
    isEditStepper,
  );

  useEffect(() => {
    if (data && !isFetching) {
      if (data.tnn_percentage_value != null) {
        setTnnPercentageValue(data.tnn_percentage_value);
      }
      setCampaignName(data.name);
      setCampaignType(data.tnn_campaign_schedules.map(val => val.type));

      let GtCompanies: ICompanyWithPricing[] = [];
      let NrCompanies: ICompanyWithPricing[] = [];
      data.tnn_campaign_schedules.forEach(val => {
        const mappedCompanies = val.companies.map(
          (company: ICompanyFromAPI) => ({
            ...company,
            share_price: company.pivot?.share_price
              ? parseFloat(company.pivot.share_price)
              : undefined,
            dollar_value: company.pivot?.dollar_value
              ? parseFloat(company.pivot.dollar_value)
              : undefined,
          }),
        );

        if (val.type === 'GameTime') {
          GtCompanies = mappedCompanies;
        } else {
          NrCompanies = mappedCompanies;
        }
      });
      setPartners({
        gametime: {selected_companies: GtCompanies},
        neptuneRadio: {selected_companies: NrCompanies},
      });
      const slotDetails: ISlotSelectionProp[] = data?.messages?.map(val => {
        const data: ISlotSelectionProp = {
          tnn_campaign_slot: val.tnn_campaign_slot ?? 0,
          label: val?.label ?? '',
          tnn_campaign_weightage: val.tnn_campaign_weightage ?? 0,
          tnn_campaign_is_primary: val.tnn_campaign_is_primary ?? false,
          file: undefined,
        };

        if (val.media_track?.url) {
          data.media_track = val.media_track;
        } else {
          data.fileName = val.prepopulated_file_name;
          data.initial_url = val.initial_url;
        }
        return data;
      });

      setParameter(prev => ({
        ...prev,
        start_date: dayjs(data.start_date),
        end_date: dayjs(data.end_date),
        number_of_runs: data.number_of_runs,
        slots: data.messages ? data.messages.length : null,
        slot_details: slotDetails ?? [],
      }));
      const built = buildCampaignStateFromData(data);
      if (!built) return;
      setCampaignName(built.campaignName);
      setCampaignType(built.campaignType);
      setAdSource(built.adSource);
      setCommercialLength(built.commercialLength);
      setType(built.type);
      setPartners(built.partners);
      setParameter(built.parameter);
    }
  }, [data, isFetching]);

  const [apiState, setApiState] = useState({
    status: '',
    error: null,
    message: '',
  });

  const isNameTypeCompleted = useCallback(() => {
    return campaignName.length > 0 && campaignType.length > 0;
  }, [campaignName, campaignType]);

  const isParametersCompleted = useCallback(() => {
    const baseValid =
      parameter.start_date && parameter.end_date && parameter.number_of_runs;

    if (!parameter.manual && parameter.slot_details.length === 0)
      return baseValid;

    let primarySet = false;
    let totalWeightage = 0;
    let fileUploadedCount = 0;

    parameter.slot_details.forEach(val => {
      if (val.file || val.media_track?.id || val.initial_url) {
        fileUploadedCount++;
      }
      if (val.tnn_campaign_is_primary) {
        primarySet = val.tnn_campaign_is_primary;
      }
      if (val.tnn_campaign_weightage) {
        totalWeightage += val.tnn_campaign_weightage;
      }
    });

    const isFileUploaded = fileUploadedCount === parameter.slot_details.length;

    return baseValid && primarySet && totalWeightage === 100 && isFileUploaded;
  }, [
    parameter.start_date,
    parameter.end_date,
    parameter.number_of_runs,
    parameter.manual,
    parameter.slot_details,
  ]);

  const isGTParamCompleted = useCallback(() => {
    return partners.gametime.selected_companies.length > 0;
  }, [partners]);
  const isNRParamCompleted = useCallback(() => {
    return partners.neptuneRadio.selected_companies.length > 0;
  }, [partners]);

  const steps = useMemo(() => {
    const stepList = [
      {
        label: 'Enter Name/Type',
        disabled: false,
        isSubmitDisabled: false,
      },
      {
        label: 'Select Parameters',
        disabled: !isNameTypeCompleted(),
        isSubmitDisabled: false,
      },
    ];
    if (campaignType.length === 0) {
      stepList.push({
        label: 'Select Partners',
        disabled: !isParametersCompleted(),
        isSubmitDisabled: false,
      });
    }
    if (campaignType.includes('GameTime')) {
      stepList.push({
        label: 'Select GameTime Partners',
        disabled: !isParametersCompleted(),
        isSubmitDisabled: false,
      });
    }
    if (campaignType.includes('Neptune Radio')) {
      stepList.push({
        label: 'Select Neptune Radio Partners',
        disabled:
          campaignType.length === 2
            ? !isGTParamCompleted()
            : !isParametersCompleted(),
        isSubmitDisabled: false,
      });
    }
    stepList.push({
      label: 'Review',
      disabled: campaignType.includes('Neptune Radio')
        ? !isNRParamCompleted()
        : !isGTParamCompleted(),
      isSubmitDisabled: false,
    });
    return stepList;
  }, [
    campaignType,
    isNameTypeCompleted,
    isGTParamCompleted,
    isNRParamCompleted,
    isParametersCompleted,
  ]);

  // option to edit the form when api is failed
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null, message: ''});
  }, []);

  const handleClose = useCallback(() => {
    setApiState({status: '', error: null, message: ''});
    closeDialog();
    navigate(`/admin/tnn/${sponsorRequestId}/campaign`);
  }, [closeDialog, navigate, sponsorRequestId]);

  const handleCompanyPriceChange = useCallback(
    (
      type: 'gametime' | 'neptuneRadio',
      companyId: number,
      sharePrice: number,
      dollarValue: number,
    ) => {
      setPartners(prev => {
        const targetKey = type === 'gametime' ? 'gametime' : 'neptuneRadio';
        const targetCompanies = prev[targetKey].selected_companies;

        const updatedCompanies = targetCompanies.map(company =>
          company.id === companyId
            ? {...company, share_price: sharePrice, dollar_value: dollarValue}
            : company,
        );

        return {
          ...prev,
          [targetKey]: {
            ...prev[targetKey],
            selected_companies: updatedCompanies,
          },
        };
      });
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null, message: ''});
    const payload = new FormData();
    payload.append(`name`, campaignName);

    if (parameter.start_date) {
      payload.append('start_date', parameter.start_date.format('YYYY-MM-DD'));
    }
    if (parameter.end_date) {
      payload.append('end_date', parameter.end_date.format('YYYY-MM-DD'));
    }
    if (parameter.number_of_runs) {
      payload.append('number_of_runs', parameter.number_of_runs.toString());
    }

    if (parameter.slot_details) {
      parameter.slot_details.forEach((val, key) => {
        payload.append(
          `multiple_slots[${key}][tnn_campaign_slot]`,
          val.tnn_campaign_slot.toString(),
        );
        payload.append(
          `multiple_slots[${key}][tnn_campaign_weightage]`,
          val.tnn_campaign_weightage.toString(),
        );
        payload.append(
          `multiple_slots[${key}][tnn_campaign_is_primary]`,
          val.tnn_campaign_is_primary ? '1' : '0',
        );
        payload.append(`multiple_slots[${key}][label]`, val.label ?? '');
        if (val.media_track?.id) {
          payload.append(
            `multiple_slots[${key}][media_track_id]`,
            val.media_track?.id.toString(),
          );
        }
        if (val.file) {
          payload.append(`multiple_slots[${key}][file]`, val.file as Blob);
        }
      });
    }

    if (campaignType.includes('GameTime')) {
      if (partners.gametime.selected_companies) {
        partners.gametime.selected_companies.forEach((company, index) => {
          payload.append(
            `gameTime[companies][${index}][id]`,
            company.id.toString(),
          );
          if (company.share_price !== undefined) {
            payload.append(
              `gameTime[companies][${index}][share_price]`,
              company.share_price.toString(),
            );
          }
          if (company.dollar_value !== undefined) {
            payload.append(
              `gameTime[companies][${index}][dollar_value]`,
              company.dollar_value.toString(),
            );
          }
        });
      }
    }

    if (campaignType.includes('Neptune Radio')) {
      if (partners.neptuneRadio.selected_companies) {
        partners.neptuneRadio.selected_companies.forEach((company, index) => {
          payload.append(
            `neptuneRadio[companies][${index}][id]`,
            company.id.toString(),
          );
          if (company.share_price !== undefined) {
            payload.append(
              `neptuneRadio[companies][${index}][share_price]`,
              company.share_price.toString(),
            );
          }
          if (company.dollar_value !== undefined) {
            payload.append(
              `neptuneRadio[companies][${index}][dollar_value]`,
              company.dollar_value.toString(),
            );
          }
        });
      }
    }
    const url =
      scheduleId && isEditStepper
        ? `/v1/tnn/sponsor/${sponsorRequestId}/campaign/${scheduleId}?_method=PUT`
        : `/v1/tnn/sponsor/${sponsorRequestId}/campaign`;
    const message =
      scheduleId && isEditStepper ? 'Campaign Updated' : 'Campaign created';
    apiPost(url, payload)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [getTnnCampaignTypesUrl(sponsorRequestId)],
          refetchType: 'active',
        });
        setApiState({
          status: 'success',
          error: null,
          message: message,
        });
      })
      .catch(error => {
        setApiState({status: 'error', error, message: ''});
      });
  }, [
    campaignName,
    campaignType,
    parameter,
    setApiState,
    sponsorRequestId,
    scheduleId,
    isEditStepper,
    queryClient,
    partners.gametime.selected_companies,
    partners.neptuneRadio.selected_companies,
  ]);

  return (
    <StepperLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onBack={onPreviousStep}
      onNext={onNextStep}
      onCancel={closeDialog}
      onSubmit={handleSubmit}
      onStepClick={onStepClick}
      isSubmitted={apiState.status !== ''}
      maxWidth={
        activeStepIndex === 4 && campaignType.length === 2 ? 'xl' : undefined
      }
      maxHeight='80%'>
      {activeStepIndex === 0 && (
        <SelectNameAndTypeCmpt
          isFetching={isFetching}
          isEditStepper={isEditStepper}
          campaignName={campaignName}
          setCampaignName={setCampaignName}
          campaignType={campaignType}
          setCampaignType={setCampaignType}
          adSource={adSource}
          commercialLength={commercialLength}
          type={type}
        />
      )}
      {activeStepIndex === 1 && (
        <ParameterSelectionCmpt
          parameter={parameter}
          setParameter={setParameter}
          isActive={data?.status === 'Active'}
          initialSlots={data?.messages.length ?? 0}
          isEdit={isEditStepper}
          showSlotTable
        />
      )}
      {activeStepIndex === 2 && campaignType.includes('GameTime') && (
        <SelectPartners
          title='Select GameTime Partners'
          keyType={'gametime'}
          partners={partners}
          setPartners={setPartners}
        />
      )}
      {((activeStepIndex === 2 && !campaignType.includes('GameTime')) ||
        (activeStepIndex === 3 &&
          campaignType.includes('Neptune Radio') &&
          campaignType.length === 2)) && (
        <SelectPartners
          title='Select Neptune Radio Partners'
          keyType={'neptuneRadio'}
          partners={partners}
          setPartners={setPartners}
        />
      )}
      {((activeStepIndex === 3 && campaignType.length === 1) ||
        (activeStepIndex === 4 && campaignType.length === 2)) && (
        <ReviewCmpt
          campaignName={campaignName}
          adSource={adSource}
          commercialLength={commercialLength}
          type={type}
          campaignType={campaignType}
          parameter={parameter}
          partners={partners}
          onCompanyPriceChange={handleCompanyPriceChange}
          tnnPercentageValue={tnnPercentageValue}
        />
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </StepperLayout>
  );
};

export default TnnCampaignCreationStepper;

export const ParameterSelectionCmpt = ({
  parameter,
  setParameter,
  isActive,
  initialSlots,
  isEdit,
  rowTwoPrefix,
  rowTwoSuffix,
  slotsClassName,
  showSlotTable = false,
}: {
  parameter: IParameterDetailsProp;
  setParameter: React.Dispatch<React.SetStateAction<IParameterDetailsProp>>;
  isActive: boolean;
  initialSlots: number;
  isEdit: boolean;
  rowTwoPrefix?: React.ReactNode;
  rowTwoSuffix?: React.ReactNode;
  slotsClassName?: string;
  showSlotTable?: boolean;
}) => {
  const [noOfRuns, setNoOfRuns] = useState<number>(
    parameter.number_of_runs ?? 1,
  );
  const [dateRangePickerValue, setDateRangePickerValue] = useState<
    DateRange<Dayjs> | [null, null]
  >([parameter.start_date ?? null, parameter.end_date ?? null]);

  const handleDateRangeChange = (newValue: DateRange<Dayjs>) => {
    setDateRangePickerValue(newValue);
  };

  const shouldDisableDate = (date: Dayjs) => {
    const disabledDates = dayjs();
    return date.isBefore(disabledDates, 'day');
  };

  const handlePrimaryToggle = useCallback(
    (slotId: number) => {
      setParameter(prev => {
        const slots = [...prev.slot_details];
        slots.forEach(val => {
          val.tnn_campaign_is_primary =
            val.tnn_campaign_slot === slotId
              ? !val.tnn_campaign_is_primary
              : false;
        });
        return {...prev, slot_details: slots};
      });
    },
    [setParameter],
  );

  const handleWeightageChange = useCallback(
    (slotId: number, value: number) => {
      setParameter(prev => {
        const slots = [...prev.slot_details];
        slots.forEach(val => {
          if (val.tnn_campaign_slot === slotId) {
            val.tnn_campaign_weightage = value;
          }
        });
        return {...prev, slot_details: slots};
      });
    },
    [setParameter],
  );

  const handleLabelChange = useCallback(
    (slotId: number, value: string) => {
      setParameter(prev => {
        const slots = [...prev.slot_details];
        slots.forEach(val => {
          if (val.tnn_campaign_slot === slotId) {
            val.label = value;
          }
        });
        return {...prev, slot_details: slots};
      });
    },
    [setParameter],
  );

  const handleFileChange = useCallback(
    (value: File | undefined, slotId: number) => {
      if (!value) return;
      if (value.type !== 'audio/mpeg') {
        Toast(alertConfig.mp3_format_error, 'warning');
        return;
      }

      setParameter(prev => ({
        ...prev,
        slot_details: prev.slot_details.map(val =>
          val.tnn_campaign_slot === slotId ? {...val, file: value} : val,
        ),
      }));
    },
    [setParameter],
  );

  const handleTrackChange = useCallback(
    (mediaTrack: IMediaTrack, slotId: number) => {
      setParameter(prev => ({
        ...prev,
        slot_details: prev.slot_details.map(val =>
          val.tnn_campaign_slot === slotId
            ? {...val, media_track: mediaTrack}
            : val,
        ),
      }));
    },
    [setParameter],
  );

  useEffect(() => {
    setParameter(prev => {
      return {
        ...prev,
        start_date: dateRangePickerValue[0],
        end_date: dateRangePickerValue[1],
        number_of_runs: noOfRuns,
      };
    });
  }, [dateRangePickerValue, noOfRuns, setParameter]);

  return (
    <Box>
      <Box className='flex items-center mb-16px' sx={{gap: 2}}>
        <DateRangePicker
          localeText={{start: 'Start Date', end: 'End Date'}}
          value={dateRangePickerValue}
          onChange={handleDateRangeChange}
          shouldDisableDate={isEdit ? undefined : shouldDisableDate}
          className='popupDatePicker'
          slotProps={{
            textField: {size: 'small', className: 'w-200'},
            fieldSeparator: {children: null, className: 'hidden'},
          }}
        />
        <Counter count={noOfRuns} setCount={setNoOfRuns} />
      </Box>
      <Box className='flex items-center mb-16px' sx={{gap: 2}}>
        {rowTwoPrefix}
        <MultipleFileUploadCmpt
          parameter={parameter}
          setParameter={setParameter}
          initialSlots={initialSlots}
          isEdit={isEdit}
          isActive={isActive}
          className={slotsClassName}
        />
        {rowTwoSuffix}
      </Box>
      {(parameter.manual || showSlotTable) && (
        <Divider className='d-block mb-2' />
      )}
      {(parameter.manual || showSlotTable) && (
        <Table aria-label='simple table' className='blue-variant15 '>
          <TableHead>
            <TableRow>
              <TableCell className='w-160'>Executions/Spots</TableCell>
              <TableCell className='w-160'>Label</TableCell>
              <TableCell className='w-120'>weight</TableCell>
              <TableCell className='w-110'>Primary</TableCell>
              <TableCell>TNN Ad File</TableCell>
              {(isEdit || showSlotTable) && (
                <TableCell align='center'>Swap File</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {parameter.slots &&
              parameter.slot_details.map(val => (
                <TableRow key={val.tnn_campaign_slot}>
                  <TableCell align='center'>{val.tnn_campaign_slot}</TableCell>
                  <TableCell>
                    <TextField
                      variant='outlined'
                      value={val.label}
                      onChange={e =>
                        handleLabelChange(val.tnn_campaign_slot, e.target.value)
                      }
                      sx={{
                        width: '145px',
                        height: '40px !important',
                        '& .MuiInputBase-root': {
                          height: '40px !important',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      hiddenLabel
                      className='w-100'
                      type='number'
                      variant='filled'
                      onChange={e => {
                        if (
                          Number(e.target.value) > -1 &&
                          Number(e.target.value) < 101
                        ) {
                          handleWeightageChange(
                            val.tnn_campaign_slot,
                            Number(e.target.value),
                          );
                        }
                      }}
                      value={val.tnn_campaign_weightage || 0}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      className='ml-2'
                      label=''
                      onChange={() =>
                        handlePrimaryToggle(val.tnn_campaign_slot)
                      }
                      control={<IOSSwitch defaultChecked />}
                      checked={Boolean(val.tnn_campaign_is_primary)}
                    />
                  </TableCell>
                  <TableCell>
                    {!val.media_track && !val.initial_url ? (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        {val.file ? (
                          <Stack direction='row' alignItems='center'>
                            <Typography className='mr-2'>
                              {val.file.name}
                            </Typography>
                            <MusicPreviewBtn
                              id={val.tnn_campaign_slot}
                              url={URL.createObjectURL(val.file)}
                              track_name={val.file.name}
                              track_artist={val.file.name}
                            />
                          </Stack>
                        ) : null}
                        <MultipleUplaodButton
                          id={val.tnn_campaign_slot}
                          handleChange={handleFileChange}
                        />
                        {/* This will directly upload the processed file and complete the campaign.  */}
                        {/* <MultipleProcessedFileUplaodButton
                        onClose={(track?: IMediaTrack) => {
                          if (track) {
                            handleTrackChange(track, val.tnn_campaign_slot);
                          }
                        }}
                      /> */}
                      </Stack>
                    ) : (
                      <Stack direction='row' alignItems={'center'}>
                        <Typography className='mr-2'>
                          {val.media_track?.file_name ?? val.fileName ?? ''}
                        </Typography>
                        <MusicPreviewBtn
                          id={val.tnn_campaign_slot}
                          url={val.media_track?.url || val.initial_url || ''}
                          track_name={
                            val.media_track?.track_name || val.fileName || ''
                          }
                          track_artist={
                            val.media_track?.track_artist || val.fileName || ''
                          }
                        />
                      </Stack>
                    )}
                  </TableCell>
                  {isEdit || showSlotTable ? (
                    <TableCell align='center'>
                      <MultipleProcessedFileUplaodButton
                        disabled={!val.media_track && !val.initial_url}
                        onClose={(track?: IMediaTrack) => {
                          if (track) {
                            handleTrackChange(track, val.tnn_campaign_slot);
                          }
                        }}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const MultipleUplaodButton = ({
  id,
  handleChange,
  disabled = false,
}: {
  id: number;
  handleChange: (value: File | undefined, id: number) => void;
  disabled?: boolean;
}) => {
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });
  const [fileDuration, setFileDuration] = useState(0);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0] as File;
        const duration = await getAudioDurationFromFile(file);
        setFileDuration(duration);
        if (duration / 60 <= 5) {
          handleChange(file, id);
        }
      }
    },
    [handleChange, id],
  );

  return (
    <Stack direction='row' alignItems='center'>
      <IconButton
        color='primary'
        disabled={disabled}
        component='label'
        role={undefined}
        title='File Upload'>
        <UploadFileOutlined />
        <VisuallyHiddenInput
          type='file'
          accept='audio/*'
          onChange={handleFileChange}
        />
      </IconButton>
      {fileDuration / 60 >= 5 && (
        <IconButton color='warning' title='Max Song Duration 5 minutes'>
          <Warning />
        </IconButton>
      )}
    </Stack>
  );
};

const MultipleProcessedFileUplaodButton = ({
  disabled = false,
  onClose,
}: {
  disabled?: boolean;
  onClose: (track?: IMediaTrack) => void;
}) => {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(
    (track?: IMediaTrack) => {
      onClose(track);
      setOpen(false);
    },
    [onClose],
  );

  return (
    <>
      <IconButton
        color='primary'
        disabled={disabled}
        component='label'
        aria-label='Processed File Upload'
        onClick={() => setOpen(true)}
        title='Processed File Upload'>
        <DriveFolderUploadIcon />
      </IconButton>
      {open && <UploadProcessedFileDialog type='' onClose={handleClose} />}
    </>
  );
};

// number count select
const options = [1, 2, 3, 4];

const MultipleFileUploadCmpt = ({
  parameter,
  setParameter,
  initialSlots,
  isEdit = false,
  isActive,
  className,
}: {
  parameter: IParameterDetailsProp;
  setParameter: React.Dispatch<React.SetStateAction<IParameterDetailsProp>>;
  initialSlots: number;
  isEdit?: boolean;
  isActive: boolean;
  className?: string;
}) => {
  const handleChange = useCallback(
    (val: number | null) => {
      setParameter(prev => {
        let fileSlots = val ? prev.slot_details : [];

        if (val) {
          let slotIncrement = fileSlots.length + 1;
          while (fileSlots.length < val && fileSlots.length < 5) {
            const isFirst = fileSlots.length === 0;
            fileSlots.push({
              tnn_campaign_slot: slotIncrement++,
              label: '',
              tnn_campaign_weightage: isFirst ? 100 : 0,
              tnn_campaign_is_primary: isFirst,
              file: undefined,
            });
          }
          fileSlots = fileSlots.slice(0, val);
        }
        return {...prev, slots: val ?? null, slot_details: fileSlots};
      });
    },
    [setParameter],
  );
  const resolvedClassName = className ?? 'w-250';
  return (
    <Autocomplete
      value={parameter.slots}
      fullWidth={!!className}
      className={resolvedClassName}
      options={options}
      autoHighlight
      disabled={isEdit && isActive}
      getOptionLabel={option => option.toString()}
      getOptionDisabled={option =>
        isEdit ? options.indexOf(option) < initialSlots - 1 : false
      }
      disableClearable={isEdit}
      onChange={(
        _: React.SyntheticEvent<Element, Event>,
        value: number | null,
      ) => {
        handleChange(value ?? null);
      }}
      renderInput={params => (
        <TextField
          {...params}
          label='No of Executions/Spots'
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  );
};

export const buildCampaignStateFromData = (
  data: ReturnType<typeof useRqListTnnCampaignSchedule>['data'],
) => {
  if (!data) return null;

  const mapCompanies = (
    companies: (ICompany & {
      pivot?: {share_price?: string; dollar_value?: string};
    })[],
  ) =>
    companies.map(company => ({
      ...company,
      share_price: company.pivot?.share_price
        ? parseFloat(company.pivot.share_price)
        : undefined,
      dollar_value: company.pivot?.dollar_value
        ? parseFloat(company.pivot.dollar_value)
        : undefined,
    }));

  let GtCompanies: ICompany[] = [];
  let NrCompanies: ICompany[] = [];
  data.tnn_campaign_schedules.forEach(val => {
    if (val.type === 'GameTime') {
      GtCompanies = mapCompanies(val.companies);
    } else {
      NrCompanies = mapCompanies(val.companies);
    }
  });

  const slotDetails: ISlotSelectionProp[] = data?.messages?.map(val => {
    const slot: ISlotSelectionProp = {
      tnn_campaign_slot: val.tnn_campaign_slot ?? 0,
      label: val?.label ?? '',
      tnn_campaign_weightage: val.tnn_campaign_weightage ?? 0,
      tnn_campaign_is_primary: val.tnn_campaign_is_primary ?? false,
      file: undefined,
    };
    if (val.media_track?.url) {
      slot.media_track = val.media_track;
    } else {
      slot.fileName = val.prepopulated_file_name;
      slot.initial_url = val.initial_url;
    }
    return slot;
  });

  return {
    campaignName: data.name,
    campaignType: data.tnn_campaign_schedules.map(val => val.type),
    adSource: data.add_source ?? 'NA',
    commercialLength:
      data.campaign_length || data.other_campaign_length || 'NA',
    type: data.type,
    partners: {
      gametime: {selected_companies: GtCompanies},
      neptuneRadio: {selected_companies: NrCompanies},
    },
    parameter: {
      start_date: dayjs(data.start_date),
      end_date: dayjs(data.end_date),
      number_of_runs: data.number_of_runs,
      slots: data.messages ? data.messages.length : null,
      slot_details: slotDetails ?? [],
      commercial_length: data.campaign_length ?? '',
      commercial_length_other: data.other_campaign_length ?? '',
      ad_source: data.add_source ?? '',
      manual: false,
    },
  };
};

export const queryDefault = {
  page: 1,
  per_page: 20,
  search: '',
  dma: [],
  state: [],
  cmc_product: [],
  product_type: [],
  secondary_product_type: [],
  condition_type: 'and',
  is_participation_enabled: true,
};

const useDividerStyles = makeStyles({
  customDivider: {
    borderBottom: '1px dashed #4d4d4d !important', // Adjust color and style as needed
    opacity: 0.5, // Adjust opacity as needed
  },
});

export const CustomDivider = () => {
  const classes = useDividerStyles();
  return <Divider className={classes.customDivider} />;
};

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;
export const StateMultipleSelect = ({
  className,
  selecteValues,
  handleChange,
}: {
  className: string;
  selecteValues: string[];
  handleChange: (type: string, selectedIds: string[]) => void;
}) => {
  const {data, isFetching, isError} = useRqListStates();
  return !isFetching && !isError && data ? (
    <Autocomplete
      className={className ? className : ''}
      multiple
      limitTags={1}
      options={data!}
      autoHighlight
      disableCloseOnSelect
      value={data.filter(option =>
        selecteValues.includes(option.id.toString()),
      )}
      getOptionLabel={option => option.name}
      onChange={(_: React.SyntheticEvent<Element, Event>, value: IState[]) => {
        handleChange(
          'state',
          value.map(v => v.id.toString()),
        );
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{marginRight: 8}}
            checked={selecteValues.includes(option.id.toString())}
          />
          {option.name}
        </li>
      )}
      renderTags={(value, getTagProps) => (
        <SelectDropdownRenderTags
          value={value.map(v => v.name)}
          getTagProps={getTagProps}
        />
      )}
      renderInput={params => (
        <TextField
          {...params}
          label='State'
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  ) : null;
};

export const PartnerSwapSelectionCmpt = ({
  query,
  selectedCompanyList,
  setSelectedCompanyList,
}: {
  query: IListAllCompaniesQueryParams;
  selectedCompanyList: ICompany[];
  setSelectedCompanyList: React.Dispatch<React.SetStateAction<ICompany[]>>;
}) => {
  const {data, isFetching, isError} = useRqListAllCompaniesList(query);
  const [availableCompanyList, setAvailableCompanyList] = useState<ICompany[]>(
    [],
  );
  const [availableCompanyChecked, setAvailableCompanyChecked] = useState<
    ICompany[]
  >([]);
  const [selectedCompanyChecked, setSelectedCompanyChecked] = useState<
    ICompany[]
  >([]);

  const selectedCompanyListRef = useRef(selectedCompanyList);
  selectedCompanyListRef.current = selectedCompanyList;

  useEffect(() => {
    if (data && !isFetching && !isError) {
      setAvailableCompanyList(prev => {
        const selectedIds = new Set(
          selectedCompanyListRef.current.map((v: ICompany) => v.id),
        );
        const next = data.filter(v => !selectedIds.has(v.id));
        if (
          next.length === prev.length &&
          next.every((v, i) => v.id === prev[i]!.id)
        )
          return prev;
        return next;
      });
    }
  }, [data, isFetching, isError]);

  const handleAvailableCompanyChecked = useCallback(
    (company: ICompany) => {
      setAvailableCompanyChecked(prev => {
        const checkedComapanies = prev.map(val => val.id);
        if (checkedComapanies.includes(company.id)) {
          return prev.filter(val => val.id !== company.id);
        } else {
          return [...prev, company];
        }
      });
    },
    [setAvailableCompanyChecked],
  );

  const handleSelectAll = useCallback(
    (companies: ICompany[]) => {
      setAvailableCompanyChecked(companies);
    },
    [setAvailableCompanyChecked],
  );

  const handleSelectedCompanyChecked = useCallback(
    (company: ICompany) => {
      setSelectedCompanyChecked(prev => {
        const checkedComapanies = prev.map(val => val.id);
        if (checkedComapanies.includes(company.id)) {
          return prev.filter(val => !checkedComapanies.includes(val.id));
        } else {
          return [...prev, company];
        }
      });
    },
    [setSelectedCompanyChecked],
  );

  const handleMoveToSelectedCompany = useCallback(() => {
    const checkedComapanies = availableCompanyChecked.map(val => val.id);
    // remove from available company
    setAvailableCompanyList(prev =>
      prev.filter(val => !checkedComapanies.includes(val.id)),
    );
    // move to selected company
    setSelectedCompanyList(prev => [...prev, ...availableCompanyChecked]);

    // empty the data
    setAvailableCompanyChecked([]);
  }, [
    availableCompanyChecked,
    setAvailableCompanyList,
    setSelectedCompanyList,
    setAvailableCompanyChecked,
  ]);

  const handleMoveToAvailableCompany = useCallback(() => {
    const checkedComapanies = selectedCompanyChecked.map(val => val.id);
    // remove from selected company
    setSelectedCompanyList(prev =>
      prev.filter(val => !checkedComapanies.includes(val.id)),
    );
    // move to available company
    setAvailableCompanyList(prev => [...prev, ...selectedCompanyChecked]);

    // empty the data
    setSelectedCompanyChecked([]);
  }, [
    selectedCompanyChecked,
    setAvailableCompanyList,
    setSelectedCompanyList,
    setSelectedCompanyChecked,
  ]);
  return (
    <Box className='blue_variant36 w-p-90'>
      <Stack direction={'row'} justifyContent={'space-between'}>
        {availableCompanyList ? (
          <CompanyListCmpt
            title='Available Partners'
            isCheckedAll={
              availableCompanyList.length === availableCompanyChecked.length &&
              availableCompanyList.length !== 0
            }
            companies={availableCompanyList}
            checkedCompanies={availableCompanyChecked}
            isLoading={isFetching}
            handleSelectAll={handleSelectAll}
            handleClicked={handleAvailableCompanyChecked}
          />
        ) : null}
        <Stack className='h-271' justifyContent={'center'} spacing={4}>
          <IconButtonWithStyles
            active={availableCompanyChecked.length > 0}
            direction='forward'
            onClick={handleMoveToSelectedCompany}
          />
          <IconButtonWithStyles
            active={selectedCompanyChecked.length > 0}
            direction='backward'
            onClick={handleMoveToAvailableCompany}
          />
        </Stack>
        <CompanyListCmpt
          title='Selected Partners'
          isCheckedAll={false}
          companies={selectedCompanyList}
          checkedCompanies={selectedCompanyChecked}
          handleSelectAll={handleSelectAll}
          handleClicked={handleSelectedCompanyChecked}
        />
      </Stack>
    </Box>
  );
};

const useArrowButtonStyles = makeStyles({
  button: {
    backgroundColor: (props: {active: boolean}) =>
      props.active ? '#008cba !important' : '#203040 !important',
    color: 'white !important',
    borderRadius: '50% !important',
    padding: '10px !important',
  },
});

type IconButtonWithStylesProps = {
  active: boolean;
  direction: 'forward' | 'backward';
  onClick: () => void;
};

export const IconButtonWithStyles: React.FC<IconButtonWithStylesProps> = ({
  active,
  direction,
  onClick,
}) => {
  const classes = useArrowButtonStyles({active});

  return (
    <IconButton
      className={classes.button}
      onClick={onClick}
      disabled={!active}
      aria-label={direction === 'forward' ? 'Move forward' : 'Move backward'}>
      {direction === 'forward' ? <ArrowForwardIcon /> : <ArrowBackIcon />}
    </IconButton>
  );
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
export const FileUploadWithChipViewCmpt = ({
  label,
  file,
  handleAction,
}: {
  label: string;
  file: {
    file: File;
    fileName: string;
  } | null;
  handleAction: (type: string, event?: ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <Box>
      <Button
        component='label'
        variant='contained'
        size='medium'
        role={undefined}
        startIcon={<CloudUploadIcon />}>
        {label}
        <VisuallyHiddenInput
          type='file'
          accept='audio/*'
          onChange={e => handleAction('upload', e)}
        />
      </Button>
      {file && file.fileName ? (
        <Chip
          label={file.fileName}
          variant='outlined'
          className='ml-2'
          onDelete={() => handleAction('delete')}
        />
      ) : null}
    </Box>
  );
};

const SkeletonCompanyListItem = () => {
  return (
    <Box>
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
      <Skeleton variant='rectangular' className='w-full h-33 mb-2' />
    </Box>
  );
};

const CompanyListCmpt = ({
  title,
  isCheckedAll,
  companies,
  checkedCompanies,
  isLoading,
  handleSelectAll,
  handleClicked,
}: {
  title: string;
  isCheckedAll: boolean;
  companies: ICompany[];
  checkedCompanies: ICompany[];
  isLoading?: boolean;
  handleSelectAll: (companies: ICompany[]) => void;
  handleClicked: (val: ICompany) => void;
}) => {
  const classes = useListStyles();
  const [isChecked, setIsChecked] = useState<boolean>(isCheckedAll);
  useEffect(() => {
    setIsChecked(isCheckedAll);
  }, [isCheckedAll]);
  const handleChecked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setIsChecked(true);
        handleSelectAll(companies);
      } else {
        setIsChecked(false);
        handleSelectAll([]);
      }
    },
    [companies, handleSelectAll],
  );

  const Row = useCallback(
    ({index, style}: {index: number; style: React.CSSProperties}) => {
      const value = companies[index] as ICompany;
      const isSuspended =
        Boolean(value.suspended_at);
      const isDeactivated =
        Boolean(value.deleted_at);
      const statusMessage = isDeactivated
        ? 'Partner is Deactivated'
        : isSuspended
        ? 'Partner is Suspended'
        : '';

      return (
        <Box style={{...style, padding: '0px 6px 6px 6px'}}>
          {/* Apply padding to the container */}
          <ListItemButton
            key={value.id}
            divider
            className={`${classes.listItemButton} ${
              checkedCompanies.some(val => val.id === value.id)
                ? classes.activeListItemButton
                : ''
            }`}
            onClick={() => handleClicked(value)}
            dense>
            <ListItemText
              primary={value.name}
              classes={{primary: classes.listItemText}}
            />
            {isDeactivated ? (
              <Tooltip title={statusMessage} arrow placement='top'>
                <DoNotDisturb
                  className='text-18 no-shrink'
                  color='error'
                />
              </Tooltip>
            ) : isSuspended ? (
              <Tooltip title={statusMessage} arrow placement='top'>
                <WarningAmber
                  className='text-18 no-shrink'
                  color='warning'
                />
              </Tooltip>
            ) : null}
          </ListItemButton>
        </Box>
      );
    },
    [
      checkedCompanies,
      classes.activeListItemButton,
      classes.listItemButton,
      classes.listItemText,
      handleClicked,
      companies,
    ],
  );

  return (
    <Box className='w-330'>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignContent={'center'}
        className={classes.stickyHeader}>
        <Typography variant='h6' className='mt-2'>
          {`${title} ( ${companies ? companies.length : 0} )`}
        </Typography>
        {title === 'Available Partners' ? (
          <Checkbox
            checked={isChecked}
            disabled={companies.length === 0}
            onChange={handleChecked}
            className='self-center'
            name={'Select All'}
            inputProps={{'aria-label': 'Availabel partners checkbox'}}
          />
        ) : null}
      </Stack>
      {!isLoading ? (
        <Lists
          className={`${classes.listContainer} ${classes.scrollbar} w-330 h-330 overflow-auto scrollbar bg-dark border-radius-0`}
          height={560}
          itemCount={companies.length}
          itemSize={44}
          width='100%'>
          {Row}
        </Lists>
      ) : (
        <List
          className={`${classes.listContainer} ${classes.scrollbar} w-330 h-330 overflow-auto scrollbar bg-dark px-2 border-radius-0`}>
          <SkeletonCompanyListItem />
        </List>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    color: 'white',
    boxShadow: 'none !important',
    gap: '2px',
  },
  counterInput: {
    width: '90px',
    '& .MuiInputBase-root': {
      paddingLeft: '2px',
      paddingRight: '2px',
      minHeight: 'unset',
      borderRadius: '12px !important',
    },
    '& .MuiInputBase-input': {
      textAlign: 'center',
      color: 'white',
      padding: '0',
      minHeight: 'unset',
    },
  },
  adornmentBtn: {
    color: 'white',
    padding: '2px',
  },
  label: {
    marginRight: '8px !important',
    color: 'white',
    flexShrink: 0,
  },
});

export const Counter = ({
  count,
  setCount,
}: {
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const classes = useStyles();

  const handleIncrement = () => {
    setCount(count + 1);
  };

  const handleDecrement = () => {
    setCount(count > 1 ? count - 1 : 1);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setCount(isNaN(value) ? 1 : value);
  };

  return (
    <Paper elevation={0} className={`${classes.container} bg-transparent`}>
      <Typography variant='body1' className={classes.label}>
        Number of Runs
      </Typography>
      <TextField
        variant='outlined'
        value={count}
        onChange={handleInputChange}
        className={classes.counterInput}
        inputProps={{
          min: 0,
          style: {color: 'white'},
          'aria-label': 'number of runs',
        }}
        InputProps={{
          startAdornment: (
            <IconButton
              className={classes.adornmentBtn}
              onClick={handleDecrement}
              aria-label='remove runs'
              size='small'>
              <RemoveIcon fontSize='small' />
            </IconButton>
          ),
          endAdornment: (
            <IconButton
              className={classes.adornmentBtn}
              onClick={handleIncrement}
              aria-label='add runs'
              size='small'>
              <AddIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />
    </Paper>
  );
};
